import { t } from 'i18next';
import { Box3, Points, Vector3 } from 'three';

import { ContextMenuItem } from '../../../../../../../react/components/skyview/context-menu';
import { DesignFeature } from '../../../../../../../react/components/skyview/right-menu/tools/design-tool-menu/sections/design-feature';
import { ProgressController } from '../../../../../../../react/utils/progress-controller';
import { PromiseAbortSignal } from '../../../../../../../react/utils/promise-abort-controller';
import { isDefined } from '../../../../../../../utils/variables';
import { RevertChange } from '../../../../../../elements/design/design-node/design-node';
import { addPoints } from '../../../../../../elements/design/design-node/utils/point-node';
import { publish, SubscriptionTopic } from '../../../../../../messaging/pubsub';
import { polygonContainsPoint2d } from '../../../../../../utilities/math-helpers';
import { DesignTool } from '../../../../../design-tool';
import { addAndCommitAction } from '../../utils/actions';
import { getPointHeight, InterpolationSnapMode } from '../../utils/point';

const activateGenerateGridFeature = (tool: DesignTool): ContextMenuItem => {
  return {
    title: t('contextMenu.functions.items.generateGrid', { ns: 'skyviewDesign' }),
    action: () => {
      tool.activeFeature = DesignFeature.GenerateGrid;
      publish(SubscriptionTopic.DesignToolMenuStateChanged, {
        designMode: false,
        snapMode: false,
        placeMode: false,
        features: true,
      });
      tool.contextMenuCallback?.();
    },
    hotkey: 'G',
  };
};

const generateGrid = async (
  tool: DesignTool,
  snapMode: InterpolationSnapMode,
  gridSize: number,
  snapHeight: number,
  polygons: Vector3[][],
  pointCloudChunks?: Points[],
  signal?: PromiseAbortSignal,
  progressController?: ProgressController,
): Promise<Vector3[]> => {
  const gridPoints: Vector3[] = [];

  progressController?.callback(
    0,
    `${progressController.step} ${
      t('section.functions.grid.saveDialog.inProgress', { ns: 'skyviewDesign' }) as string
    }`,
  );

  const isPointWithinAnyPolygon = (point: Vector3) =>
    polygons.some((polygon) => polygonContainsPoint2d(point, polygon));

  if (gridPoints.length === 0) {
    // Get extents
    const bbox = new Box3().setFromPoints(polygons.flat());

    const bboxSize = bbox.getSize(new Vector3());
    const totalPoints = (bboxSize.x / gridSize) * (bboxSize.y / gridSize);
    let pointCount = 0;

    for (let x = bbox.min.x; x < bbox.max.x; x += gridSize) {
      for (let y = bbox.min.y; y < bbox.max.y; y += gridSize) {
        const point = new Vector3(x, y, 0);
        progressController?.callback(pointCount / totalPoints);
        if (signal?.aborted) {
          return [];
        }

        if (isPointWithinAnyPolygon(point)) {
          const height = await getPointHeight(
            tool,
            point,
            snapMode,
            pointCloudChunks ?? [],
            snapHeight,
          );
          if (isDefined(height)) {
            gridPoints.push(tool.asset.designNode.getLocalPosition(point).setZ(height));
          }
        }

        pointCount++;
      }
    }
  }

  progressController?.incrementStep();

  return gridPoints;
};

const applyGenerateGridResult = (tool: DesignTool, result: Vector3[]): ContextMenuItem => {
  return {
    title: t('contextMenu.functions.items.generateGrid', { ns: 'skyviewDesign' }),
    action: () => {
      let changes: RevertChange[] = [];
      const activeAttributes = tool.getActiveAttributes();

      addAndCommitAction(tool, {
        commit: () => {
          changes = addPoints(tool.asset.designNode, result, activeAttributes);
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
        rollback: () => {
          tool.asset.designNode.revert(changes);
          tool.asset.designNode.refresh();
          tool.asset.refreshMetadata();
        },
      });
      tool.contextMenuCallback?.();
    },
    hotkey: 'G',
  };
};

export const gridFunctions = {
  activateGenerateGridFeature,
  generateGrid,
  applyGenerateGridResult,
};
